import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MekormaLogo } from "./BookingPlatform.jsx";

/**
 * /m/:code or /manage/:token — "Manage your booking", linked from the Microsoft Bookings
 * confirmation email (see api/shared/manage-token.js). Shows the booking and
 * lets the customer cancel it or reschedule it through this app — instead of
 * Microsoft's own booking page, which lists every service in the business.
 *
 * Cancel reports success only once Bookings confirms it. Reschedule opens the
 * original booking link with the service preselected; the new time is booked
 * first, then this booking is cancelled (booking-create's reschedule path).
 */

const C = { brand: "#004e42", text: "#0F172A", sub: "#64748B", faint: "#94A3B8", line: "#E2E8F0" };

const fmt = (iso, opts) => {
  try { return new Date(iso).toLocaleString(undefined, opts); } catch { return iso; }
};

export default function ManageBooking() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, booking: null, cancelToken: null });
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`/api/booking/manage?t=${encodeURIComponent(token || "")}`, { cache: "no-store" })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!alive) return;
        if (!r.ok) setState({ loading: false, error: body.error || "We couldn't load this booking.", booking: null, cancelToken: null });
        else setState({ loading: false, error: null, booking: body.booking, cancelToken: body.cancelToken });
      })
      .catch(() => alive && setState({ loading: false, error: "We couldn't load this booking. Please try again.", booking: null, cancelToken: null }));
    return () => { alive = false; };
  }, [token]);

  const b = state.booking;

  const cancel = async () => {
    if (cancelling || !b) return;
    setCancelling(true); setCancelError(null);
    try {
      const r = await fetch("/api/booking/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appointmentId: b.appointmentId, businessId: b.businessId, cancelToken: state.cancelToken }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || "We couldn't cancel this booking. Please try again.");
      }
      setCancelled(true); setConfirming(false);
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const reschedule = () => navigate(`/mek/${encodeURIComponent(b.linkId)}?reschedule=${encodeURIComponent(token)}`);

  const btn = (primary, danger) => ({
    flex: 1, padding: "11px 18px", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer",
    border: primary ? "none" : `1.5px solid ${danger ? "#FCA5A5" : C.line}`,
    background: primary ? (danger ? "#EF4444" : C.brand) : "#fff",
    color: primary ? "#fff" : danger ? "#DC2626" : "#374151",
  });

  return (
    <div style={{ minHeight: "100vh", background: "#F5FAFA", fontFamily: "-apple-system,'Segoe UI',system-ui,sans-serif", padding: "32px 16px" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}><MekormaLogo height={28} /></div>

        <div style={{ background: "#fff", border: `1.5px solid ${C.line}`, borderRadius: 16, padding: "28px 24px" }}>
          {state.loading && <p style={{ textAlign: "center", color: C.sub, margin: 0 }}>Loading your booking…</p>}

          {!state.loading && state.error && (
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontSize: 20, color: C.text, margin: "0 0 8px" }}>We couldn't open this booking</h1>
              <p style={{ fontSize: 14, color: C.sub, margin: "0 0 4px", lineHeight: 1.6 }}>{state.error}</p>
              <p style={{ fontSize: 13, color: C.faint, margin: 0 }}>Questions? <a href="mailto:bookings@mekorma.com" style={{ color: C.brand }}>bookings@mekorma.com</a></p>
            </div>
          )}

          {!state.loading && b && cancelled && (
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontSize: 20, color: C.text, margin: "0 0 8px" }}>Booking cancelled</h1>
              <p style={{ fontSize: 14, color: C.sub, margin: 0, lineHeight: 1.6 }}>Your {b.serviceName} booking has been cancelled. A confirmation has been sent to <strong style={{ color: C.text }}>{b.customerEmail}</strong>.</p>
              {b.linkId && (
                <button onClick={() => navigate(`/mek/${encodeURIComponent(b.linkId)}`)} style={{ ...btn(true), flex: "none", marginTop: 20 }}>Book a new time</button>
              )}
            </div>
          )}

          {!state.loading && b && !cancelled && (
            <>
              <h1 style={{ fontSize: 20, color: C.text, margin: "0 0 4px" }}>Your booking</h1>
              <p style={{ fontSize: 13, color: C.sub, margin: "0 0 18px" }}>{b.customerName ? `${b.customerName} · ` : ""}{b.customerEmail}</p>

              <div style={{ background: "#F8FAFC", border: `1px solid ${C.line}`, borderRadius: 12, padding: "14px 16px", marginBottom: 18 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 10 }}>{b.serviceName}</div>
                {[
                  ["Date", fmt(b.startUtc, { weekday: "long", month: "long", day: "numeric", year: "numeric" })],
                  ["Time", `${fmt(b.startUtc, { hour: "numeric", minute: "2-digit" })} – ${fmt(b.endUtc, { hour: "numeric", minute: "2-digit", timeZoneName: "short" })}`],
                  ...(b.staff?.length ? [["With", b.staff.map((s) => s.displayName).join(", ")]] : []),
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", gap: 12, fontSize: 13, padding: "3px 0" }}>
                    <span style={{ width: 48, color: C.faint, fontWeight: 600 }}>{k}</span>
                    <span style={{ color: C.text }}>{v}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                {b.linkId && <button onClick={reschedule} style={btn(true)}>Reschedule</button>}
                <button onClick={() => { setCancelError(null); setConfirming(true); }} style={btn(false, true)}>Cancel booking</button>
              </div>
              {!b.linkId && (
                <p style={{ fontSize: 12, color: C.faint, margin: "10px 0 0" }}>To change the time, cancel this booking and book again, or email bookings@mekorma.com.</p>
              )}
            </>
          )}
        </div>
      </div>

      {confirming && b && (
        <div onClick={() => !cancelling && setConfirming(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", maxWidth: 400, width: "100%" }}>
            <h2 style={{ fontSize: 18, color: C.text, margin: "0 0 8px", textAlign: "center" }}>Cancel booking?</h2>
            <p style={{ fontSize: 14, color: C.sub, textAlign: "center", margin: "0 0 18px" }}>This can't be undone.</p>
            {cancelError && (
              <div role="alert" style={{ background: "#FFF1F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "#991B1B", lineHeight: 1.5 }}>
                {cancelError} Your booking is still active.
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button disabled={cancelling} onClick={() => setConfirming(false)} style={{ ...btn(false), opacity: cancelling ? 0.6 : 1 }}>Keep it</button>
              <button disabled={cancelling} onClick={cancel} style={{ ...btn(true, true), opacity: cancelling ? 0.8 : 1 }}>
                {cancelling ? "Cancelling…" : cancelError ? "Try again" : "Cancel booking"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
